import React, { useState, useCallback, useEffect } from 'react';
import { invoiceAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';

const fmt  = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const fmtK = n => { const v=Number(n||0); return v>=100000?'Rs.'+(v/100000).toFixed(1)+'L':v>=1000?'Rs.'+(v/1000).toFixed(0)+'K':'Rs.'+v.toFixed(0); };

const AGING_BUCKETS = [
  { key:'current',    label:'Current',    color:'text-green-600',  bg:'bg-green-50 border-green-200'  },
  { key:'days_1_30',  label:'1–30 Days',  color:'text-yellow-600', bg:'bg-yellow-50 border-yellow-200'},
  { key:'days_31_60', label:'31–60 Days', color:'text-orange-600', bg:'bg-orange-50 border-orange-200'},
  { key:'days_61_90', label:'61–90 Days', color:'text-red-600',    bg:'bg-red-50 border-red-200'      },
  { key:'over_90',    label:'Over 90d',   color:'text-red-800',    bg:'bg-red-100 border-red-300'     },
];
const BUCKET_BADGE = { current:'bg-green-50 text-green-700', days_1_30:'bg-yellow-50 text-yellow-700', days_31_60:'bg-orange-50 text-orange-700', days_61_90:'bg-red-50 text-red-700', over_90:'bg-red-100 text-red-800' };

export function OutstandingReport() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoiceAPI.reportOutstanding();
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows||[]).filter(r=>
    !search || r.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.invoiceNumber?.toLowerCase().includes(search.toLowerCase())
  );
  const aging = data?.aging || {};

  return (
    <div className="space-y-5">
      <PageHeader title="Outstanding Receivables" subtitle="All unpaid invoices with aging analysis"/>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {AGING_BUCKETS.map(b=>(
            <div key={b.key} className={`rounded-2xl border p-4 ${b.bg}`}>
              <p className="text-xs font-semibold text-gray-500 uppercase">{b.label}</p>
              <p className={`text-xl font-bold mt-1 ${b.color}`}>{fmtK(aging[b.key]||0)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{fmt(aging[b.key]||0)}</p>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex justify-between items-center">
          <div>
            <p className="text-sm font-semibold text-amber-800">Total Outstanding Receivables</p>
            <p className="text-xs text-amber-600 mt-0.5">{rows.length} invoices pending payment</p>
          </div>
          <p className="text-3xl font-bold text-amber-700">{fmt(data.totalOutstanding)}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 items-center justify-between">
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by customer or invoice number..."
            className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          <button onClick={load} disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loading && <div className="p-16 text-center"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/></div>}

        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Invoice No.','Customer','Invoice Date','Due Date','Total','Paid','Outstanding','Overdue By','Aging'].map(h=>(
                  <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left last:text-right">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">{data?'No outstanding invoices':'Click Refresh to load'}</td></tr>}
                {rows.map((r,i)=>(
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono font-semibold text-indigo-600 text-xs">{r.invoiceNumber}</td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-800">{r.customer?.name||'—'}</p>
                      <p className="text-xs text-gray-400">{r.customer?.phone}</p>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{r.invoiceDate?new Date(r.invoiceDate).toLocaleDateString('en-IN'):'—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{r.dueDate?new Date(r.dueDate).toLocaleDateString('en-IN'):'—'}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{fmt(r.grandTotal)}</td>
                    <td className="px-4 py-2.5 text-green-600">{fmt(r.paidAmount)}</td>
                    <td className="px-4 py-2.5 font-bold text-amber-600">{fmt(r.dueAmount)}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{r.daysPastDue>0?`${r.daysPastDue} days`:'—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`text-xs px-2 py-1 rounded-lg font-medium ${BUCKET_BADGE[r.agingBucket]||'bg-gray-100 text-gray-600'}`}>
                        {r.agingBucket?.replace(/_/g,' ')||r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-200">
                  <tr>
                    <td className="px-4 py-2.5 text-gray-800" colSpan={4}>TOTAL ({rows.length})</td>
                    <td className="px-4 py-2.5 text-gray-800">{fmt(rows.reduce((s,r)=>s+r.grandTotal,0))}</td>
                    <td className="px-4 py-2.5 text-green-700">{fmt(rows.reduce((s,r)=>s+r.paidAmount,0))}</td>
                    <td className="px-4 py-2.5 text-amber-700 text-base">{fmt(rows.reduce((s,r)=>s+r.dueAmount,0))}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
